import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { ErrorMessage } from "@hookform/error-message";
import { yupResolver } from "@hookform/resolvers/yup";
import CloseIcon from "@mui/icons-material/Close";
import {
  Alert,
  AlertTitle,
  Box,
  Dialog,
  DialogContent,
  DialogContentText,
  IconButton,
  Typography,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import InvestNowStepper from "../ui/investNowStepper";

import { updateData } from "../../features/data/data.slice";
import { numberToCurrency } from "../../libs/helpers";
import HelpBasicInvestment from "../dialogs/helpBasicInvestment";
import HelpHedgeFunds from "../dialogs/helpHedgeFunds";
import HelpRetirementAnnuity from "../dialogs/helpRetirementAnnuity";

import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useGA4React } from "ga-4-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTitle } from "../../providers/titleProvider";
import PrimaryButton from "../ui/buttons/primaryButton";
import FundSelector from "../ui/controls/fundSelector";
import DropdownInfoBlock from "../ui/feedback/dropdownInfoBlock";

import {
  completeStep,
  setRequireHelp,
  updateStep,
} from "../../features/stepper.slice";
import {
  createSubmition,
  getSubmition,
  saveSubmition,
  updateSubmition,
} from "../../features/submition/submition.slice";
import HelpTaxFreeSavings from "../dialogs/helpHelpTaxFreeSavings";
import SaveProcess from "../dialogs/saveProcess";
import DropdownInfoBlockIcon from "../ui/feedback/dropdownInfoBlockIcon";
import Checkout from "../ui/forms/fields/checkbox";
import { Report, ReportPageView } from "../../libs/loggers";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3, 0),
  },
  title: {
    marginTop: "30px",
    marginBottom: "20px",
    textAlign: "center",
  },
  closeButton: {
    position: "absolute",
    top: 5,
    right: 5,
  },
  dialog: {
    minHeight: "250px",
    textAlign: "center",
    background: theme.palette.mode === "light" ? "#fff" : "#373434",
  },
  radioTable: {
    marginTop: theme.spacing(3),
  },
  checkbox: {
    marginTop: theme.spacing(3),
  },
  input: {
    margin: theme.spacing(1, 1),
  },
}));

function InvestSolution() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const ga4 = useGA4React();

  const logEvent = (action, label, category) => {
    ga4.event(action, label, category);
  };

  const { setTitle } = useTitle();

  const [selectedFunds, setSelectedFunds] = useState([]);
  const [showTFSAMaxWarning, setShowTFSAMaxWarning] = useState(false);
  const [alertSaveProcess, setAlertSaveProcess] = useState(false);
  const [saveProcess, setSaveProcess] = useState(false);
  const funds = useSelector((state) =>
    state.investVehicle.value.funds.filter(
      (f) => f.vehicles.indexOf(state.data.value.vehicle) !== -1
    )
  );

  const [filteredFunds, setFilteredFunds] = useState([]);
  const [filteredCategory, setFilteredCategory] = useState("all");

  const vehicle = useSelector((state) =>
    state.investVehicle.value.vehicles.find(
      (v) => v.name === state.data.value.vehicle
    )
  );

  const helpDialog = useSelector((state) => state.data.value.helpDialog);
  const savedFunds = useSelector((state) => state.data.value.funds);
  const tfsaWarning = useSelector((state) => state.data.value.tfsaMaxWarning);

  const requireHelp = useSelector((state) => state.stepper.value.requireHelp);
  const stepper = useSelector((state) => state.stepper.value);
  const [searchParams, setSearchParams] = useSearchParams();
  const submitionUserId = useSelector((state) => state.submition.data?.userId);
  const userId = useSelector((state) => state.submition.data?.userId);
  const submitionId = useSelector(
    (state) => state.submition.submition?.submitionId
  );
  const submitionData = useSelector((state) => state.submition.data);
  let page = window.location.pathname.replace(/^\/|\/$/g, "");
  const schema = yup.object().shape({
    funds: yup
      .array()
      .min(1, "Please select at least one fund.")
      .required("Please select at least one fund."),
    tfsaMaxWarning: yup
      .boolean()
      .test(
        "checkTFSAMaxWarning",
        "Please confirm that you understand the TFSAs maximum contribution limit.",
        function (val) {
          let isValid = false;
          if (vehicle.id === 3 && showTFSAMaxWarning) {
            if (val) isValid = true;
          } else {
            isValid = true;
          }
          return isValid;
        }
      ),
  });

  const {
    register,
    handleSubmit,
    trigger,
    setValue,
    control,
    formState: { errors },
  } = useForm({
    mode: "onBlur",
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    if (submitionId) {
      dispatch(getSubmition(submitionId));
    }
  }, [submitionId]);

  useEffect(() => {
    if (!submitionUserId) {
      setAlertSaveProcess(true);
    }
  }, []);

  useEffect(() => {
    //Cechk QUery String
    //vehicle=unit-trust&help=true

    if (searchParams.has("help")) {
      const requireHelp = searchParams.get("requireHelp");
      dispatch(setRequireHelp({ requireHelp }));
    }

    if (searchParams.has("vehicle")) {
      const vehicle = searchParams.get("vehicle");
      dispatch(updateData({ vehicle }));
    }
  }, [searchParams]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    let page = window.location.pathname.replace(/^\/|\/$/g, "");
    ReportPageView(page, userId, submitionId);
  }, []);

  useEffect(() => {
    if (filteredFunds.length === 0) {
      setFilteredFunds(funds);
    }
  }, [funds]);

  useEffect(() => {
    setTitle("Select Investment Solution");
  }, [setTitle]);

  useEffect(() => {
    register("funds");
    register("tfsaMaxWarning");

    if (savedFunds) {
      setValue("funds", savedFunds);
    }

    if (tfsaWarning) {
      setValue("tfsaMaxWarning", tfsaWarning);
      setShowTFSAMaxWarning(true);
    }
  }, [register]);

  const onFundsUpdate = (funds) => {
    //Get total lumpsum and monthly contributions
    const totalLumpsum = funds.reduce((total, fund) => total + fund.lumpsum, 0);

    const totalMonthly = funds.reduce((total, fund) => total + fund.monthly, 0);

    //Combined total
    const total = totalLumpsum
      ? totalLumpsum
      : 0 + totalMonthly
      ? totalMonthly
      : 0;

    if (total > 36000 && vehicle.id === 3) {
      setShowTFSAMaxWarning(true);
    } else {
      setShowTFSAMaxWarning(false);
    }

    setValue("funds", funds, { shouldValidate: true });
  };

  const onSubmit = async (data) => {
    const eventInfo = {
      action: "submit",
      category: "investment-solution",
      description: "submit investment solution",
      value: {
        currentPage: page,
        step: stepper.active + 1,
        value: "select_investment_solution",
        funds: data.funds,
      },
    };

    Report(eventInfo, userId, submitionId);

    dispatch(updateData(data));
    const submition = { ...submitionData };
    if (submitionUserId) {
      try {
        if (!submition._id) {
          //Create Submition
          await dispatch(
            createSubmition({
              ...submition,
              ...data,
            })
          )
            .unwrap()
            .then((originalPromiseResult) => {})
            .catch(() => {});
        } else {
          await dispatch(
            updateSubmition({
              ...submition,
              ...data,
              submitionId: submition._id,
            })
          )
            .unwrap()
            .then((originalPromiseResult) => {})
            .catch(() => {});
        }
      } catch (err) {
        console.log(err);
      }
    }
  };

  const handleNext = () => {
    if (!helpDialog) handleSubmit(onSubmit)();
  };

  const onValidation = async () => {
    let isValid = await trigger();
    if (helpDialog) isValid = true;

    return isValid;
  };

  const onHelpNext = async (data) => {
    await dispatch(
      saveSubmition({
        ...submitionData,
        helpDialog: data,
        requireHelp: requireHelp,
      })
    );

    try {
      if (submitionData._id) {
        await dispatch(
          updateSubmition({
            ...submitionData,
            helpDialog: data,
            submitionId: submitionData._id,
            requireHelp: requireHelp,
          })
        );
      }
    } catch (err) {
      console.log(err);
    }
  };

  const onHelpSubmit = (data) => {
    console.log(data);
    const newActiveStep = stepper.active + 1;
    const splitFundIndex = data.funds.findIndex((f) => f.fund.split);

    let f = [];
    if (splitFundIndex !== -1) {
      let splitFund = data.funds[splitFundIndex];

      splitFund.fund.split.map((comp) => {
        const getFund = funds.find((f) => f.id === comp.id);

        f.push({
          fund: getFund,
          lumpsum:
            splitFund.lumpsum > 0
              ? splitFund.lumpsum * (comp.percent / 100)
              : 0,
          monthly:
            splitFund.monthly > 0
              ? splitFund.monthly * (comp.percent / 100)
              : 0,
          isHelp: true,
        });
      });
    } else {
      f.push(...data.funds);
    }

    dispatch(updateData({ funds: f, helpDialog: data }));
    dispatch(updateStep(newActiveStep));
    dispatch(completeStep(stepper.active));
    navigate(stepper.steps[newActiveStep].url);
    logEvent(
      "next_step",
      stepper.steps[newActiveStep].name,
      `Step ${newActiveStep + 1}`
    );
  };

  const theme = useTheme();

  const openSave = () => {
    logEvent("click", "save_application", `application`);
    const eventInfo = {
      action: "click",
      category: "application-save",
      description: "open save application",
      value: {
        currentPage: page,
        step: stepper.active + 1,
        value: "save_application",
      },
    };
    Report(eventInfo, userId, submitionId);
    setSaveProcess(true);
  };

  const closeSave = () => {
    logEvent("cancel", "save_application", `application`);
    const eventInfo = {
      action: "click",
      category: "application-save",
      description: "clicked no thanks",
      value: {
        currentPage: page,
        step: stepper.active + 1,
        value: "no_save_application",
      },
    };
    Report(eventInfo, userId, submitionId);
    setAlertSaveProcess(false);
    setSaveProcess(false);
  };

  const mobile = useMediaQuery(theme.breakpoints.down("sm"));
  const stepperContent = () => {
    return (
      <Box className={classes.root}>
        <Typography variant="h4" mt={1} mb={mobile ? 3 : 1}>
          {vehicle
            ? `Select your fund(s) for your ${vehicle.name}`
            : "Loading..."}
        </Typography>

        <DropdownInfoBlock
          title={"How to choose my fund"}
          content={
            "Your choice of an investment solution(s) / fund(s) should reflect the features of your investment goal: the time horizon you have available to achieve your goal(s) and the degree of risk you're willing to take. Time and risk are connected, so you can more investing over the long-term."
          }
          defaultOpen={false}
        />

        <Typography variant={"h5"} mt={mobile ? 3 : 2} fontWeight={500}>
          Investment minimum(s) per fund:
        </Typography>

        {vehicle?.name === "Tax-Free Savings Account" ? (
          <Box>
            <Typography variant={"body1"}>
              {numberToCurrency(vehicle?.minimums?.lumpsum, 0)} minimum initial
              lumpsum investment{" "}
              {vehicle?.minimums?.lumpsumMax &&
                " / Maximum investment of " +
                  numberToCurrency(vehicle?.minimums?.lumpsumMax, 0)}
              {""}
              {vehicle?.minimums?.monthly > 0 && ", OR"}
            </Typography>
            {vehicle?.minimums?.monthly > 0 && (
              <Typography variant={"body1"}>
                {numberToCurrency(vehicle?.minimums?.monthly, 0)} minimum
                monthly debit order{" "}
                {vehicle?.minimums?.monthlyMax &&
                  "and " + numberToCurrency(vehicle?.minimums?.monthlyMax, 0)}
                {" maximum debit order"}
              </Typography>
            )}
          </Box>
        ) : (
          <Box>
            <Typography variant={"body1"}>
              {numberToCurrency(vehicle?.minimums?.lumpsum, 0)} initial lumpsum
              investment{" "}
              {vehicle?.minimums?.lumpsumMax &&
                " / Maximum investment of " +
                  numberToCurrency(vehicle?.minimums?.lumpsumMax, 0)}
              {""}
              {vehicle?.minimums?.monthly > 0 && ", OR"}
            </Typography>
            {vehicle?.minimums?.monthly > 0 && (
              <Typography variant={"body1"}>
                {numberToCurrency(vehicle?.minimums?.monthly, 0)} monthly debit
                order{" "}
                {vehicle?.minimums?.monthlyMax &&
                  " / Maximum of " +
                    numberToCurrency(vehicle?.minimums?.monthlyMax, 0)}
                {""}
              </Typography>
            )}
          </Box>
        )}

        {vehicle?.name === "Tax-Free Savings Account" && (
          <Box mt={2}>
            <DropdownInfoBlockIcon
              title={
                "Two contribution limits apply: R36,000 per annum and R500,000 over your lifetime, with 40% tax payable on the amount in excess of these limits."
              }
              content={`It's important to note that the contributions are tracked and not the net value of the investment. 
              <br /><br />For example: You contribute R500,000 and have withdrawn a total of R150,000 over your lifetime. 
              <br />Your lifetime contribution limit of R500,000 has been reached, regardless of the net value of the investment`}
              defaultOpen={false}
            />
          </Box>
        )}

        {requireHelp & (vehicle?.name === "Basic Investment") ? (
          <Box
            display={"flex"}
            justifyContent={"flex-start"}
            alignItems={"center"}
            sx={{ mt: 3, mb: 3 }}
          >
            <HelpBasicInvestment
              minimums={vehicle?.minimums || null}
              onDataSubmit={(data) => onHelpSubmit(data)}
              onHelpNext={(data) => onHelpNext(data)}
            />
          </Box>
        ) : null}

        {requireHelp & (vehicle?.name === "Retirement Annuity") ? (
          <Box
            display={"flex"}
            justifyContent={"flex-start"}
            alignItems={"center"}
            sx={{ mt: 3, mb: 3 }}
          >
            <HelpRetirementAnnuity
              minimums={vehicle?.minimums || null}
              onDataSubmit={(data) => onHelpSubmit(data)}
              onHelpNext={(data) => onHelpNext(data)}
            />
          </Box>
        ) : null}

        {requireHelp & (vehicle?.name === "Tax-Free Savings Account") ? (
          <Box
            display={"flex"}
            justifyContent={"flex-start"}
            alignItems={"center"}
            sx={{ mt: 3, mb: 3 }}
          >
            <HelpTaxFreeSavings
              minimums={vehicle?.minimums || null}
              onDataSubmit={(data) => onHelpSubmit(data)}
              onHelpNext={(data) => onHelpNext(data)}
            />
          </Box>
        ) : null}

        <Box>
          {!requireHelp & !helpDialog ? (
            <FundSelector
              onFundsUpdate={(f) => onFundsUpdate(f)}
              savedFunds={savedFunds}
            />
          ) : null}
        </Box>

        {!helpDialog?.funds && (
          <ErrorMessage
            errors={errors}
            name="funds"
            render={({ message }) => (
              <Typography color="error">{message}</Typography>
            )}
          />
        )}

        <Box
          display={"flex"}
          justifyContent={"flex-start"}
          alignItems={"center"}
          sx={{ mt: 3, mb: 3 }}
        >
          {requireHelp & (vehicle?.name === "Retail Investor Hedge Fund") ? (
            <HelpHedgeFunds
              hfType={"RIHF"}
              onDataSubmit={(data) => onHelpSubmit(data)}
            />
          ) : null}

          {requireHelp & (vehicle?.name === "Qualified Investor Hedge Fund") ? (
            <HelpHedgeFunds
              hfType={"QIHF"}
              onDataSubmit={(data) => onHelpSubmit(data)}
            />
          ) : null}
        </Box>

        {helpDialog?.funds ? (
          <Box sx={{ mt: 2 }}>
            <Typography>The funds you selected are:</Typography>
            {helpDialog.funds.map((f, index) => (
              <Box key={index} sx={{ m: 2 }}>
                <Typography>{f.fund.name}</Typography>
                <Typography>
                  {f.lumpsum
                    ? "Lump Sum: " + numberToCurrency(f.lumpsum)
                    : null}
                </Typography>
                <Typography>
                  {f.monthly
                    ? "Debit Order: " + numberToCurrency(f.monthly)
                    : null}
                </Typography>
              </Box>
            ))}
          </Box>
        ) : null}

        <Box>
          {showTFSAMaxWarning && (
            <Alert
              severity={errors.tfsaMaxWarning ? "error" : "warning"}
              sx={{ m: 2 }}
            >
              <AlertTitle>Warning - Annual Contribution Limit</AlertTitle>
              <Typography>
                There is an annual contribution limit of R36,000. Should you
                continue a 40% tax payable on the amount in excess of this will
                be raised
              </Typography>
              <Checkout
                name={"tfsaMaxWarning"}
                label="I understand and want to proceed."
                control={control}
                color={errors.tfsaMaxWarning ? "error" : "warning"}
              />
            </Alert>
          )}
        </Box>
        <Dialog
          fullWidth
          PaperProps={{ classes: { root: classes.dialog } }}
          open={alertSaveProcess}
          onClose={() => closeSave(false)}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          {" "}
          <IconButton
            size="large"
            aria-label="close"
            onClick={() => closeSave()}
            className={classes.closeButton}
          >
            <CloseIcon fontSize="meduim" />
          </IconButton>
          <DialogContent
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "end",
            }}
          >
            <Typography
              className={classes.title}
              variant="h4"
              id="alert-dialog-title"
            >
              Would you like the ability to save and <br />
              continue your application at a later stage?
            </Typography>{" "}
            <DialogContentText color="primary">
              By selecting no, your progress will not be saved.
            </DialogContentText>
            <Box
              style={{
                display: "flex",
                justifyContent: "center",
                marginTop: "20px",
              }}
            >
              <PrimaryButton color="secondary" onClick={() => closeSave()}>
                No Thanks
              </PrimaryButton>
              <PrimaryButton onClick={() => openSave()}>
                Yes Please
              </PrimaryButton>
            </Box>
          </DialogContent>
        </Dialog>

        <SaveProcess
          open={saveProcess}
          openSave={() => openSave()}
          closeSave={() => closeSave()}
        />
      </Box>
    );
  };

  return (
    <InvestNowStepper
      selectedStep={1}
      getStepContent={() => stepperContent()}
      checkValidation={() => onValidation()}
      onNext={() => handleNext()}
    />
  );
}

export default InvestSolution;
